import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';

const formatDeductionKey = (key) => {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

const formatAsCurrency = (value) => {
  if (!value && value !== 0) return '$ 0.00';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return `$ ${numValue.toFixed(2)}`;
};
const PayrollDeductionsAccordion = ({ deductionCategories, deductions, editing, handleChange, EditableCell }) => {
  const renderDeductionField = (field) => (
    <Box
      key={field}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.5,
        my: 0.5,
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
        {formatDeductionKey(field)}
      </Typography>
      {editing ? (
        <Box sx={{ width: '120px' }}>
          <EditableCell
            value={deductions[field] || '0'}
            onChange={(newValue) => handleChange(field, newValue)}
            // The EditableCell component should handle input formatting internally
          />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatAsCurrency(deductions[field])}
        </Typography>
      )}
    </Box>
  );

  const renderSection = (section) => {
    if (!section.fields || section.fields.length === 0) return null;

    return (
      <Box key={section.title} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary', bgcolor: '#f5f5f5', p: 1 }}>
          {section.title}
        </Typography>
        <Box sx={{ pl: 2 }}>
          {section.fields.map(field => renderDeductionField(field))}
        </Box>
      </Box>
    );
  };

  const renderSubcategory = (subcategory) => (
    <Box key={subcategory.title} sx={{ mb: 3 }}>
      <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary
          expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
          sx={{
            mb: 1,
            fontWeight: 'bold',
            bgcolor: '#e0e0e0',
            borderRadius: '4px 4px 0 0',
            '&.Mui-expanded': { minHeight: 48 }
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {subcategory.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper elevation={0} sx={{ p: 1, borderRadius: '0 0 4px 4px', bgcolor: '#f9f9f9' }}>
            {subcategory.fields && (
              <Box sx={{ pl: 2 }}>
                {subcategory.fields.map(field => renderDeductionField(field))}
              </Box>
            )}
            {subcategory.sections && Object.values(subcategory.sections).map(section => renderSection(section))}
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  return (
    <>
      {Object.entries(deductionCategories).map(([key, category]) => (
        <Accordion key={key} defaultExpanded sx={{ mb: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
            sx={{
              bgcolor: '#e0e0e0',
              borderRadius: '4px',
              '&.Mui-expanded': { minHeight: 48 }
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {category.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, py: 2 }}>
            <Box sx={{ pl: 2 }}>
              {Object.values(category.subcategories).map(subcategory =>
                renderSubcategory(subcategory)
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default PayrollDeductionsAccordion;